/**
 * bluetoothPrinter — Web Bluetooth (BLE) pipe for the ESC/POS raster that
 * escposPrinter.js builds. Same bytes as the USB path; only the transport differs.
 *
 * Verified on-device 2026-09-30: Mac + Chrome → Mond's printer over BLE printed
 * the Thai raster test cleanly. Long-bill speed is not measured yet.
 *
 * Limits (docs/thermal-printer-setup.md §Bluetooth):
 *  - BLE only. Bluetooth Classic (SPP) printers never show up in the chooser.
 *  - Chromium only (desktop + Android). iOS/Safari have no Web Bluetooth.
 *  - One GATT write carries ~20–512 bytes, so a bill goes out in many small
 *    chunks — seconds, versus the near-instant USB bulk transfer.
 *  - The page only gets a BluetoothDevice from the chooser, or from
 *    navigator.bluetooth.getDevices() where Chrome ships it. Without it, after a
 *    reload someone has to press Connect once (a user gesture) before printing.
 *
 * Kept free of pdfmake/escposPrinter imports — POSLayout and the settings UI load it.
 */
import { reactive, readonly } from 'vue';

// GATT services seen on BLE receipt/label printers. The chooser uses
// acceptAllDevices (many printers don't advertise a service UUID), and Chrome
// then only lets the page open the services listed here.
export const PRINTER_SERVICES = [
  '000018f0-0000-1000-8000-00805f9b34fb', // generic Chinese POS/receipt (char 2af1)
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Xprinter-style / many 58mm portables
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip/ISSC transparent UART (dual-mode modules)
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 style UART
  '0000ae30-0000-1000-8000-00805f9b34fb', // "cat" printers
  '0000ae00-0000-1000-8000-00805f9b34fb',
  '0000af30-0000-1000-8000-00805f9b34fb',
  '0000fee7-0000-1000-8000-00805f9b34fb',
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART
  '0000e0ff-3c17-d293-8e48-14fe2e4da212',
];

// Send-speed presets, picked per device in printer settings (btSpeed): bytes per
// GATT write + pause after each write-without-response chunk.
//
// Chrome resolves writeValueWithoutResponse without real flow control, so writes
// fired faster than the printer drains them overflow its queue and chunks are
// DROPPED silently — no error, the print stops midway, and the printer is left
// mid-raster (next job prints garbage until it's power-cycled).
//
// Findings on-device 2026-09-30 (Mac + Chrome, Mond's printer):
//  - 180 B unpaced dropped even on a text-only slip: it's how OFTEN we write.
//  - 512 B unpaced passed every text test slip, then jammed on REAL receipts:
//    the head slows on dark rows (PromptPay/review QR, bold), so the printer
//    drains data slower there — how fast it can take data depends on the bill.
// So there is no unpaced preset at all: every preset keeps a pause, and the
// long test page carries QR-dense blocks to be at least as dark as a receipt.
//  - normal: small chunks, paced — ≈ 15–20 KB/s, a ~115 KB 80mm bill ≈ 6–8 s.
//  - fast:   big chunks, paced — ~5× fewer writes, ≈ 30 KB/s. The DEFAULT
//            (printerSettings) since it passed the dense long test and real QR
//            receipts on-device; needs the printer to take 512-byte writes.
// A chunk bigger than the printer's MTU can't go at all — a printer that
// garbles on fast needs normal. No auto-tuning: nothing reports a garbled print.
export const BT_SPEEDS = {
  normal: { chunk: 100, delayMs: 5 },
  fast: { chunk: 512, delayMs: 15 },
};

const STORAGE_KEY = 'escpos_bt_printer'; // { id, name } of the last picked printer

function getStored() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch (e) { return null; }
}
function setStored(value) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch (e) { /* ignore */ }
}

// Reactive so the printer icon / settings status follow a connect or a drop
// (printer switched off, walked out of range) without a reload.
// ackWrites = the printer only offers write-WITH-response: every chunk waits for
// an acknowledgement round trip, so it's slow whatever the speed preset says.
const state = reactive({ connected: false, name: getStored()?.name || '', ackWrites: false });
export const bluetoothStatus = readonly(state);

let device = null;   // BluetoothDevice for this page lifetime
let target = null;   // { char, withoutResponse } — the characteristic we write to
let inflight = null; // one reconnect at a time (POSLayout + a print can race)

// เช็คจาก API ที่มีจริง ไม่ดู user agent: Safari/Chrome บน iPhone/iPad ไม่มี navigator.bluetooth
// อยู่แล้ว (WebKit ไม่ทำ Web Bluetooth) แต่แอปเบราว์เซอร์อย่าง Bluefy ที่ bridge ไป CoreBluetooth
// มีให้ — บล็อกด้วย UA จะปิดทางเดียวที่ iPad พิมพ์ตรงได้ (ยังไม่ได้ทดสอบกับ Bluefy จริง)
export function canUseBluetooth() {
  return typeof navigator !== 'undefined' && !!navigator.bluetooth;
}

/**
 * Does this machine actually have a Bluetooth adapter? navigator.bluetooth exists
 * in desktop Chrome even on a PC with no Bluetooth at all — requestDevice() then
 * rejects with NotFoundError ("Bluetooth adapter not available"), the same error
 * as a dismissed chooser, so without this check the button just does nothing.
 * Resolves true when the browser can't tell (older Chrome) — the chooser decides.
 */
export async function bluetoothAdapterAvailable() {
  if (!canUseBluetooth()) return false;
  if (typeof navigator.bluetooth.getAvailability !== 'function') return true;
  try { return await navigator.bluetooth.getAvailability(); } catch (e) { return true; }
}

/** Follow a USB Bluetooth dongle being plugged in/out. Returns the unsubscribe. */
export function onBluetoothAvailabilityChange(cb) {
  if (!canUseBluetooth() || typeof navigator.bluetooth.addEventListener !== 'function') return () => {};
  const handler = (event) => cb(!!event.value);
  navigator.bluetooth.addEventListener('availabilitychanged', handler);
  return () => navigator.bluetooth.removeEventListener('availabilitychanged', handler);
}

/** Can Chrome hand the paired printer back after a reload (getDevices)? */
export function canRememberBluetooth() {
  return canUseBluetooth() && typeof navigator.bluetooth.getDevices === 'function';
}

/** A BluetoothDevice for this page lifetime exists — reconnecting needs no chooser. */
export function hasBluetoothHandle() {
  return !!device;
}

function onDisconnected() {
  target = null;
  state.connected = false;
}

function adopt(dev) {
  if (device === dev) return;
  if (device) device.removeEventListener('gattserverdisconnected', onDisconnected);
  device = dev;
  dev.addEventListener('gattserverdisconnected', onDisconnected);
}

// Connect and find something to write to. Prefer write-without-response: it's
// the fast path; plain write waits for an ack on every chunk.
async function connect(dev) {
  const server = dev.gatt.connected ? dev.gatt : await dev.gatt.connect();
  let services = [];
  try { services = await server.getPrimaryServices(); } catch (e) { services = []; }
  const writable = [];
  for (const s of services) {
    const chars = await s.getCharacteristics().catch(() => []);
    for (const c of chars) {
      if (c.properties.writeWithoutResponse || c.properties.write) {
        writable.push({ char: c, withoutResponse: !!c.properties.writeWithoutResponse });
      }
    }
  }
  const found = writable.find(w => w.withoutResponse) || writable[0];
  if (!found) {
    try { dev.gatt.disconnect(); } catch (e) { /* ignore */ }
    const err = new Error('This Bluetooth device has no printer service we can write to');
    err.name = 'NotAPrinterError';
    throw err;
  }
  target = found;
  state.connected = true;
  state.ackWrites = !found.withoutResponse;
  state.name = dev.name || state.name || 'Bluetooth';
}

/**
 * Show Chrome's Bluetooth chooser and connect to the picked printer. Must run
 * from a user gesture. Rejects with NotFoundError when the chooser is dismissed,
 * NotAPrinterError when the device has nothing writable.
 */
export async function requestBluetoothPrinter() {
  const dev = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICES,
  });
  adopt(dev);
  await connect(dev);
  setStored({ id: dev.id, name: dev.name || '' });
  return dev;
}

/**
 * Reconnect without a chooser: the device object from earlier in this page, or
 * the remembered one via getDevices() after a reload. Resolves false when there
 * is nothing to reconnect to (caller decides whether to ask for a gesture).
 */
export function reconnectBluetoothPrinter() {
  if (inflight) return inflight;
  inflight = (async () => {
    if (target && device?.gatt.connected) return true;
    let dev = device;
    if (!dev && canRememberBluetooth()) {
      const stored = getStored();
      const list = await navigator.bluetooth.getDevices();
      dev = (stored && list.find(d => d.id === stored.id)) || null;
    }
    if (!dev) return false;
    adopt(dev);
    await connect(dev);
    return true;
  })().finally(() => { inflight = null; });
  return inflight;
}

/**
 * Get a writable connection for a print job. Returns { picked } — the device if
 * the chooser had to be shown (so the caller can re-guess the paper width).
 * promptIfMissing:false is for prints nobody clicked (auto-print after payment):
 * those must never raise a chooser mid-shift.
 */
export async function openBluetooth({ promptIfMissing = true } = {}) {
  try {
    if (await reconnectBluetoothPrinter()) return { picked: null };
  } catch (e) {
    // printer off / out of range — fall through to an explicit pick
  }
  if (!promptIfMissing) throw new Error('Bluetooth printer not connected');
  return { picked: await requestBluetoothPrinter() };
}

/**
 * Send bytes in GATT-sized chunks at the given BT_SPEEDS preset. Resolves
 * { bytes, ms } so the settings test print can show the real throughput;
 * rejects if the printer drops mid-job.
 */
export async function writeBluetooth(bytes, speed = 'normal') {
  if (!target) throw new Error('Bluetooth printer not connected');
  const { chunk, delayMs } = BT_SPEEDS[speed] || BT_SPEEDS.normal;
  const { char, withoutResponse } = target;
  const started = Date.now();
  for (let i = 0; i < bytes.length; i += chunk) {
    // slice (copy) — NOT subarray (a view into the whole job). Chrome honours a
    // view's offset, but Bluefy on iPhone/iPad bridges the value to CoreBluetooth
    // by its underlying ArrayBuffer and sent the ENTIRE job on every chunk: the
    // printer printed the receipt once per chunk (~260×) — seen on-device
    // 2026-09-30. Each chunk must own its buffer.
    const part = bytes.slice(i, i + chunk);
    if (withoutResponse) {
      await (char.writeValueWithoutResponse ? char.writeValueWithoutResponse(part) : char.writeValue(part));
      if (delayMs) await new Promise(resolve => setTimeout(resolve, delayMs));
    } else {
      await (char.writeValueWithResponse ? char.writeValueWithResponse(part) : char.writeValue(part));
    }
  }
  return { bytes: bytes.length, ms: Date.now() - started };
}

// ---- ถามให้กดเชื่อมต่อตอนเข้า POS (เบราว์เซอร์ที่จำเครื่องข้ามการเปิดใหม่ไม่ได้) ----

const CONNECT_SKIP_KEY = 'printer.bt_connect_skipped';

/** กด "ไว้ทีหลัง" แล้ว ไม่ถามซ้ำจนกว่าจะปิดแท็บ (sessionStorage) */
export function skipBluetoothConnectPrompt() {
  try { sessionStorage.setItem(CONNECT_SKIP_KEY, '1'); } catch (e) { /* ignore */ }
}

export function shouldPromptBluetoothConnect() {
  if (state.connected) return false;
  try { return sessionStorage.getItem(CONNECT_SKIP_KEY) !== '1'; } catch (e) { return true; }
}
